// Used in notifications.component.html

// Angular
import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { OWL_DATE_TIME_FORMATS } from "@danielmoncada/angular-datetime-picker";
import { ContentChange } from "ngx-quill";

// Services/Types/Helpers
import { FormHelper } from "#core/helpers";
import { Course, Language, Notification, PPUser as User } from "cli-types";

// RXJS/Store
import * as fromStore from "#login/store";
import { select, Store } from "@ngrx/store";
import { Observable } from "rxjs";

// Error State
import { MyErrorStateMatcher } from "./send-notification.errorstate";

// https://momentjs.com/docs/#/displaying/
export const MY_CUSTOM_FORMATS = {
    parseInput: "L LT",
    fullPickerInput: "L LT",
    datePickerInput: "LL",
    timePickerInput: "LT",
    monthYearLabel: "MMM YYYY",
    dateA11yLabel: "LL",
    monthYearA11yLabel: "MMMM YYYY",
};

@Component({
    selector: "send-notification",
    templateUrl: "./send-notification.component.html",
    styleUrls: [
        "../../notifications.component.scss",
        "./send-notification.component.scss",
    ],
    providers: [
        {
            provide: OWL_DATE_TIME_FORMATS,
            useValue: MY_CUSTOM_FORMATS,
        },
    ],
    standalone: false
})
export class SendNotificationComponent implements OnInit {
    matcher = new MyErrorStateMatcher();

    @Input() courses?: Course[];
    @Input() languages?: Language[];
    @Input() isMaximized = false;

    @Output()
    create = new EventEmitter<Notification>();

    @Output()
    cancel = new EventEmitter<any>();

    notificationForm?: UntypedFormGroup;
    profileData?: User;
    working = false;
    quillTipBool = false;

    isTeacher: Observable<boolean>;
    isDistributor: Observable<boolean>;

    tiers = [
        { displayName: "Premium", value: "premium" },
        { displayName: "Essential", value: "lite" },
        { displayName: "All", value: "both" },
    ];

    constructor(
        private fb: UntypedFormBuilder,
        private formHelper: FormHelper,
        public store: Store<fromStore.UserState>
    ) {
        this.isTeacher = this.store.pipe(select(fromStore.getIsTeacher));
        this.isDistributor = this.store.pipe(
            select(fromStore.getIsDistributor)
        );

        this.store.pipe(select(fromStore.getUserProfile)).subscribe((data) => {
            if (data) {
                this.profileData = data;
                this.initForm();
            }
        });
    }

    initTeacherForm() {
        this.notificationForm = this.fb.group({
            body: [
                "",
                [
                    Validators.required,
                    this.formHelper.noQuillEditorWhitespaceValidator,
                ],
            ],
            title: ["", Validators.required],
            displayAt: [null],
            expireAt: [null],
            courses: [null, Validators.required],
            sendToStudents: [true],
            "all-users": [false],
        });
    }

    initDistributorForm() {
        this.notificationForm = this.fb.group({
            body: ["", Validators.required],
            title: ["", Validators.required],
            displayAt: [null],
            expireAt: [null],
            sendToStudents: [true, Validators.required],
            sendToTeachers: [true, Validators.required],
            languages: [""],
            tiers: ["both"],
            "all-users": [true],
        });
    }

    initForm() {
        if (this.profileData && this.profileData.roleLevel === 1) {
            // Distributor
            this.initDistributorForm();
        } else {
            this.initTeacherForm();
        }
    }

    ngOnInit() {
        this.initForm();
    }

    sendNotification() {
        if (this.notificationForm?.valid) {
            this.working = true;
            // Convert language form response into an array
            if (this.profileData && this.profileData.roleLevel === 1) {
                const selectedLanguageID =
                    this.notificationForm.value.languages;
                if (selectedLanguageID.length === 0) {
                    this.notificationForm.value.languages = [];
                } else {
                    this.notificationForm.value.languages = [
                        selectedLanguageID,
                    ];
                }
            }
            this.create.emit(this.notificationForm.value);
        }
    }

    onClickCancel() {
        this.cancel.emit();
    }

    keyHandler(event: KeyboardEvent) {
        const target =
                event.currentTarget instanceof HTMLElement
                    ? event.currentTarget
                    : null,
            associatedElemId = target ? target.getAttribute("aria-owns") : null, // grab the id of the associated datepicker,
            associatedElem = associatedElemId
                ? document.getElementById(associatedElemId)
                : null; // check to see if the element exists;
        if (target && [13, 32].indexOf(event.keyCode) > -1 && !associatedElem) {
            target.click(); // if the element doesn't exist, click to open a new one
        }
    }

    setBody(editor: ContentChange) {
        if (this.notificationForm) {
            // Force form to recalculate.
            this.notificationForm.updateValueAndValidity();
        }
    }

    qeFocusToggle(event: MouseEvent) {
        const targat =
                event.target instanceof HTMLElement ? event.target : null,
            targatClass =
                targat && targat.hasAttribute("class")
                    ? targat.getAttribute("class")
                    : null;
        this.quillTipBool = !!(
            targatClass && targatClass.indexOf("ql-editor") > -1
        );
    }

    qeKeyHandler(event: KeyboardEvent) {
        const targit =
                event.target instanceof HTMLElement ? event.target : null,
            targitClass =
                targit && targit.hasAttribute("class")
                    ? targit.getAttribute("class")
                    : null;
        if (targit && targitClass && targitClass.indexOf("ql-editor") > -1) {
            if (event.keyCode === 34 && event.altKey) {
                const nextElem = document.getElementsByClassName(
                    "send-notification__displayAt"
                );
                if (nextElem && nextElem.length) {
                    nextElem[0].getElementsByTagName("input")[0].focus(); // The user has hit page down within the editor. Send focus to the next input.
                }
                this.quillTipBool = false;
            } else {
                this.quillTipBool = true;
            }
        } else if (targit) {
            this.quillTipBool = false;
            /* PAS-4594: This is because quill editor is not honoring these attributes. */
            const editorElem = targit.getElementsByClassName("ql-editor");
            const alignLabels = targit.getElementsByClassName("ql-align");
            const textAlignString = "Text Alignment";
            let alignLabelElem, options, y, z;

            for (y = 0; y < alignLabels.length; y++) {
                if (
                    alignLabels[y].getElementsByClassName("ql-picker-label")
                        .length
                ) {
                    alignLabelElem =
                        alignLabels[y].getElementsByClassName(
                            "ql-picker-label"
                        )[0]; // There will only ever be 1. Grab it
                    alignLabelElem.setAttribute("aria-label", textAlignString);
                    alignLabelElem.setAttribute("title", textAlignString);
                }
                if (
                    alignLabels[y].getElementsByClassName("ql-picker-options")
                        .length
                ) {
                    options =
                        alignLabels[y].getElementsByClassName("ql-picker-item");
                    for (z = 0; z < options.length; z++) {
                        const dateValue =
                            options[z].hasAttribute("data-value") &&
                            options[z].getAttribute("data-value")
                                ? options[z].getAttribute("data-value")
                                : null;
                        if (dateValue) {
                            options[z].setAttribute("aria-label", dateValue);
                            options[z].setAttribute("title", dateValue);
                        } else {
                            options[z].setAttribute("aria-label", "Left"); // The default state is aligned left, which has no data-value.
                            options[z].setAttribute("title", "Left");
                        }
                    }
                }
            }
            if (
                editorElem.length &&
                editorElem[0].getAttribute("contenteditable")
            ) {
                editorElem[0].setAttribute(
                    "aria-describedby",
                    "quill-tip-text"
                ); // There will only ever be 1.
                editorElem[0].setAttribute("role", "textbox");
                editorElem[0].setAttribute("aria-multiline", "true");
            }
        }
    }

    dateMismatch() {
        if (this.notificationForm) {
            const displayAt = this.notificationForm.value.displayAt;
            const expireAt = this.notificationForm.value.expireAt;
            if (displayAt && expireAt) {
                return displayAt > expireAt;
            }
        }
    }
}
